/**
 * MC All Stats -- fleet-wide sortable/searchable DataTable (Phase 248, D-03/D-07/D-08/D-09/D-10/
 * D-12/D-13).
 *
 * Initializes on /mc/all-stats only (guards on the `.datatable[data-table="mc-all-stats"]`
 * element's presence). `initAllStats()` is called UNCONDITIONALLY from `mc.ts`'s `static init()`
 * on every /mc/* page, so a second guard -- an init marker stamped on the table element itself --
 * prevents a double DataTables bind on repeated calls.
 *
 * D-03: the travel-distance column COUNT is fleet-dependent, so the column list is never
 * hardcoded here. It is read from the server-rendered `data-column-catalog` JSON attribute
 * (all-stats.latte, fed by `McAnalyticsModel::getAllStatsColumns()`) and used to build the
 * DataTables `columns` array at runtime, including the D-13 default-sort column index.
 *
 * ⚠ THE ATTRIBUTE IS DELIBERATELY NAMED `data-column-catalog`, NOT `data-columns`. DataTables'
 * constructor reads EVERY `data-*` attribute off the bound `<table>` element via jQuery's
 * `$(table).data()` and merges the result into its own init options (a documented DataTables
 * feature -- options are settable via matching data attributes). `data-columns` is the exact
 * HTML-attribute spelling of DataTables' own `columns` init option, so naming this attribute
 * `data-columns` let DataTables silently overwrite the carefully-built `columns` config array
 * built below with the raw, wrong-shaped catalog array -- a live prod incident (empty table,
 * `_fnColumnOptions` crash on an out-of-bounds column index). Any future custom data attribute on
 * a DataTables-bound `<table>` must avoid every DataTables/jQuery option name -- `columns`,
 * `order`, `paging`, `searching`, and the rest -- not just this one.
 *
 * ⚠ EVERY CELL CARRYING A USER- OR SERVER-CONTROLLED STRING (Player display name, Server name) IS
 * RENDERED THROUGH `DataTable.render.text()`. The property this codebase's other DataTable-driven
 * pages have been burned by for raw string assignment is used NOWHERE in this file, deliberately
 * and by grep-enforced acceptance criterion -- matching leaderboard.ts's own discipline. The
 * per-metric-column `d` display string is formatted server-side
 * (`McAnalyticsModel::formatAllStatsCell()` -- number_format/duration/ratio/distance_cm) and
 * carries no untrusted substring, so it is returned as-is from a plain `render` function.
 *
 * ⚠ SIGNAL URL IS THE LITERAL `?do=getAllStats`, never a Latte link macro -- a template macro has
 * no meaning in a .ts file. Signal name = handler name minus `handle`, first letter lowercased:
 * handleGetAllStats -> `?do=getAllStats`. The `X-Requested-With` header is REQUIRED on that
 * request; Nette's `isAjax()` check depends on it, and `BasePresenter::processSignal()` throws
 * `InvalidLinkException` ("All handles should be AJAX requests") without it -- matching
 * leaderboard.ts's and analytics.ts's own ajax configs exactly.
 *
 * D-08: the Server column is addressed by NAME (`columns[].name = 'server'`), never a numeric
 * index -- D-03's variable column count means the Server column's positional index is not stable
 * across fleets. (Phase 249 retired this file's own `table.column('server:name')` client-side
 * search call site along with the free-text filter it drove -- see the Plan 10 addendum below --
 * but the addressed-by-name discipline itself still stands for anything that DOES query the
 * column.)
 *
 * D-12: em-dash (unmeasured) cells are tagged via `createdCell`, reading the RAW `rowData[key].v`
 * -- never the already-formatted `d` display string -- so a genuinely absent value can be styled
 * and titled independently of its text. Sort order itself is entirely server-side
 * (`McAnalyticsModel::getAllStatsTable()`'s own null-last comparator); this module sets no
 * client-side multi-sort option, only the single initial `order` DataTables needs to show the
 * correct arrow and issue the first request with (D-13's Playtime-descending default.
 *
 * ── Phase 249 Plan 10 — D-04/D-05/D-07/D-08/D-09/D-10/D-11 ──────────────────────────────────────
 * The Player and Server columns keep their `data`/`name`/`render` exactly as Phase 248 left them
 * (so the server-side sort key and global search still address them by their existing string
 * values) and gain a `createdCell` callback each that swaps the rendered text for a 32px linked
 * avatar tile plus the display name (D-15, gap-closure Plan 12 -- see below), mirroring
 * `leaderboard.ts`'s `playerCell()` tile-construction idiom. D-07: the player link is pre-scoped to
 * THAT ROW'S OWN server via the BRACKET `?server[]=` form (not `leaderboard.ts`'s legacy scalar
 * `?server=` -- the profile page now reads a list). D-08: the server link matches the `Mc:detail`
 * route mask (`/mc/<key>`). Every value reaches the DOM through `createElement`/`textContent`/
 * attribute assignment, never the markup-assignment property -- matching this file's own
 * player/server-name discipline above. `server_icon` is used directly as `img.src` with zero client-side
 * re-validation: `App\Mc\FaviconUrl::sanitize()` already ran server-side (Plan 249-05) and is the
 * single control (T-249-I-01) -- a second, weaker client check would only create a second place
 * for the rule to drift.
 *
 * ── Phase 249 gap-closure Plan 12 — D-15 ────────────────────────────────────────────────────────
 * Reverses this same plan's icon-only reading directly above: both identity cells now render the
 * display name as VISIBLE text after the 32px icon, inside the SAME anchor as the icon -- a
 * superset of leaderboard.ts's name-only anchor, chosen so the icon does not silently stop being
 * clickable (it already was). Phase 239's D-13 ("the name is the anchor where a name exists") is
 * now satisfied in its detail-list form rather than its fleet-card form. The now-redundant
 * `aria-label` is dropped from both cells -- the anchor has visible accessible text, and a
 * duplicate `aria-label` can only drift from it; `title` stays as the full-name tooltip for the
 * ellipsis-truncated case, capped by a scoped CSS max-width in mc.scss so a long modded name can
 * never widen the table.
 *
 * The Server picker replacing Phase 248's free-text substring filter (D-09/D-10/D-11) binds a NEW
 * DOM id, distinct from every other `/mc/*` picker's own so this page's query can never
 * double-bind a shared module (see `initAllStats()` below for the exact id). The checked-server
 * state and its URL persistence follow the exact `leaderboard.ts`/`feed.ts`/`analytics.ts` idiom:
 * `server[]=` read merged with the legacy singular `server=`, `CSS.escape`-guarded option lookup,
 * and a non-history-appending URL state write -- never a browser storage API, never server-side
 * (T-249-STATE-01).
 */

import DataTable from 'datatables.net-bs5';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** One entry from `data-column-catalog` -- McAnalyticsModel::getAllStatsColumns()'s own shape. */
interface AllStatsColumn {
    key: string;
    label: string;
    unit: string;
}

/** One metric cell on the wire -- McAnalyticsModel::getAllStatsTable()'s per-column entry shape.
 *  `v` is the raw float|null value (null = never measured, D-12); `d` is the server-formatted
 *  display string (already unit-aware, never re-derived client-side).
 *  Phase 273 D-09/AFK-03 (Plan 12): `r`, an OPTIONAL per-cell absence reason, present ONLY on the
 *  Active Playtime column's own null cells (getAllStatsTable()'s own "sibling field on the Active
 *  cell only" -- every other column's cell shape is untouched). Absent (or itself null) on an
 *  older cached bundle / a genuinely-unmeasured player -- createdCell() below falls back to
 *  UNMEASURED_TITLE in either case, never throwing on a missing key. */
interface AllStatsCell {
    v: number | null;
    d: string;
    r?: string | null;
}

/** Same wording leaderboard.ts's appendValueCell() uses for the identical em-dash concept, so the
 *  "unmeasured" visual language never diverges between the two pages. */
const UNMEASURED_TITLE = 'Not measured for this player — unranked, not zero.';

/** The identity portion of a getAllStatsTable() row (Plan 249-05) -- declared explicitly rather
 *  than read through the generic Record<string, unknown> shape every other cell's createdCell
 *  uses, so a typo in a field name is a tsc error rather than a silently-undefined href. */
interface AllStatsIdentityRow {
    player: string;
    player_uuid: string;
    server: string;
    server_key: string;
    server_icon: string | null;
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

/** D-10's active window preset, sent as the `window` AJAX param on every reload. */
let currentWindow = 'all-time';

/** D-09/D-10/D-11: the checked server keys, or `[]` -- the D-10 "all servers" state. Every
 *  request re-validates `server[]` server-side through resolveScopedServerIds(); the client-side
 *  option-existence check in initAllStats() below is a UX affordance only. */
let currentServers: string[] = [];

// ---------------------------------------------------------------------------
// Avatar cell builders (D-04/D-05/D-07/D-08)
// ---------------------------------------------------------------------------

/**
 * D-04/D-07/D-15: a 32px player-head avatar tile plus the visible, truncating display name,
 * mirroring leaderboard.ts's playerCell() tile-construction idiom (same mc-heads.net URL shape,
 * loading="lazy", 32x32, empty alt, the visibility-hiding error listener). Unlike
 * leaderboard.ts's name-only anchor, the single <a> here wraps BOTH the tile and the name -- the
 * head stays clickable, which it already was before D-15 (Phase 239's D-13 is now satisfied in
 * its detail-list form).
 */
function buildPlayerCell(row: AllStatsIdentityRow): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'mc-board-player';

    const link = document.createElement('a');
    // D-07: the BRACKET form, not leaderboard.ts's legacy scalar -- the profile page now reads a
    // list, and every all-stats row knows exactly one owning server. An empty key omits the
    // suffix entirely rather than emitting a scope that reads as "scoped to nothing".
    const scopeSuffix = row.server_key !== ''
        ? `?server[]=${encodeURIComponent(row.server_key)}`
        : '';
    link.href = `/mc/players/${encodeURIComponent(row.player_uuid)}${scopeSuffix}`;
    link.title = row.player;

    const tile = document.createElement('span');
    tile.className = 'mc-avatar-tile';
    const img = document.createElement('img');
    img.src = `https://mc-heads.net/avatar/${encodeURIComponent(row.player_uuid)}/32`;
    img.loading = 'lazy';
    img.width = 24;
    img.height = 24;
    img.alt = '';
    img.className = 'mc-player-avatar';
    // Visibility, not display -- the placeholder background shows through with zero layout shift.
    // Guaranteed rendering for a non-Mojang uuid (mc-heads.net always 404s for one), not a rare
    // fallback.
    img.addEventListener('error', () => { img.style.visibility = 'hidden'; });
    tile.appendChild(img);
    link.appendChild(tile);

    const name = document.createElement('span');
    name.className = 'mc-board-name';
    name.textContent = row.player;
    link.appendChild(name);

    wrap.appendChild(link);
    return wrap;
}

/**
 * D-05/D-08/D-15: a 32px server-icon avatar tile plus the visible, truncating display name,
 * matching the Mc:detail route mask. `server_icon` is used directly as `img.src` -- already
 * sanitized server-side via FaviconUrl::sanitize() (Plan 249-05) -- with no client-side
 * re-validation added (T-249-I-01). When `server_icon` is null, no <img> is appended at all and
 * the tile's own placeholder background stands alone. When `server_key` is empty, the tile plus
 * name render WITHOUT an anchor rather than linking to a broken path (T-249-10-T1).
 */
function buildServerCell(row: AllStatsIdentityRow): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'mc-board-player';

    const tile = document.createElement('span');
    tile.className = 'mc-server-icon-tile';
    if (row.server_icon !== null) {
        const img = document.createElement('img');
        img.src = row.server_icon;
        img.loading = 'lazy';
        img.width = 24;
        img.height = 24;
        img.alt = '';
        img.addEventListener('error', () => { img.style.visibility = 'hidden'; });
        tile.appendChild(img);
    }

    const name = document.createElement('span');
    name.className = 'mc-board-name';
    name.textContent = row.server;

    if (row.server_key === '') {
        wrap.appendChild(tile);
        wrap.appendChild(name);
        return wrap;
    }

    const link = document.createElement('a');
    link.href = `/mc/${encodeURIComponent(row.server_key)}`;
    link.title = row.server;
    link.appendChild(tile);
    link.appendChild(name);
    wrap.appendChild(link);
    return wrap;
}

// ---------------------------------------------------------------------------
// Column config (D-03/D-12/D-13, Phase 273 Plan 12)
// ---------------------------------------------------------------------------

/**
 * Builds the DataTables `columns` config array from the server's column catalog, plus the D-13
 * default-sort index. Extracted out of `initAllStats()` (which previously built this inline) so it
 * is a PURE, DOM-free, DataTables-free function `tests/mc/all-stats-columns.test.ts` can import and
 * exercise directly -- this repo's own coverage-risk directive: "import and exercise the real
 * exported function so it cannot drift," rather than re-typing the column-building logic into a
 * test. Behavior is byte-identical to what `initAllStats()` built inline before this extraction.
 *
 * D-12/Plan 273-12 (D-13's fifth of five mirrored flip sites -- the other four:
 * McPresenter::LEADERBOARD_DEFAULT_METRIC, ::ALL_STATS_DEFAULT_SORT, ::PROFILE_CHART_DEFAULT_METRIC,
 * rosterSort.ts's DEFAULT_SORT_KEY): the default-sort resolution flips from matching the plain
 * Playtime column key to matching `active_playtime` (see the `if` below). The `Playtime` column
 * itself is UNTOUCHED here -- it stays in
 * `catalog` (fed by `McMetrics::metricCatalog()`, unmodified by this plan) and keeps its own
 * `orderable: true`, independently clickable-sortable (REQUIREMENTS D-07). Defensive fallback,
 * structural not added: `defaultSortIndex` starts at `2` (the first catalog column, right after the
 * fixed Player/Server pair) and is only ever OVERWRITTEN when a column with the target key is
 * actually found in this fleet's catalog -- if a future catalog change ever drops `active_playtime`
 * entirely, this resolves to whatever the first catalog column is rather than an out-of-range
 * index, which DataTables would silently mis-sort on (an out-of-range order index is a silent
 * mis-sort, not a visible error).
 *
 * The recorded `rowCallback`/last-cell-clobber gotcha
 * (`feedback_datatables_rowcallback_lastcell_clobber_on_new_column`) does NOT apply here: every
 * `createdCell` below is keyed by `col.key` (`rowData[col.key]`), never a positional index or a
 * `:last-child`/`cells[...]` lookup -- confirmed by grep (`tools/probe-273-12-allstats-columns.mjs`)
 * finding zero positional-addressing patterns in this file, and independently by the multi-column,
 * multi-row cross-check `tests/mc/all-stats-columns.test.ts` performs against this exact function.
 */
export function buildAllStatsColumns(catalog: AllStatsColumn[]): { columns: any[]; defaultSortIndex: number } {
    const columns: any[] = [
        {
            data: 'player',
            name: 'player',
            render: DataTable.render.text(),
            createdCell: (cell: HTMLTableCellElement, _cellData: unknown, rowData: AllStatsIdentityRow) => {
                cell.textContent = '';
                cell.appendChild(buildPlayerCell(rowData));
            },
        },
        {
            data: 'server',
            name: 'server',
            render: DataTable.render.text(),
            createdCell: (cell: HTMLTableCellElement, _cellData: unknown, rowData: AllStatsIdentityRow) => {
                cell.textContent = '';
                cell.appendChild(buildServerCell(rowData));
            },
        },
    ];

    // D-13 (Plan 273-12): default sort resolved from the parsed catalog by NAME
    // ('active_playtime'), never a bare literal index -- the travel-mode column count varies per
    // fleet, so a hardcoded index would silently point at the wrong column (or none) the moment the
    // catalog shape changes.
    let defaultSortIndex = 2;
    catalog.forEach((col, i) => {
        const columnIndex = i + 2; // +2 for the fixed Player/Server columns ahead of the catalog
        if (col.key === 'active_playtime') {
            defaultSortIndex = columnIndex;
        }

        columns.push({
            data: col.key,
            name: col.key,
            orderable: true,
            render: (data: AllStatsCell | undefined) => data?.d ?? '',
            createdCell: (cell: HTMLTableCellElement, _cellData: unknown, rowData: Record<string, unknown>) => {
                const raw = rowData[col.key] as AllStatsCell | undefined;
                if (raw && raw.v === null) {
                    cell.classList.add('mc-board-unmeasured');
                    // Phase 273 D-09/AFK-03 (Plan 12): a per-cell reason (server-blind / pre-rollout,
                    // AFK-04/AFK-06) rides on the Active Playtime cell's own sibling `r` field; every
                    // other column's cell never carries one, so this always falls back to the
                    // pre-existing, UNCHANGED UNMEASURED_TITLE -- byte-identical to every other
                    // absent cell's title today.
                    cell.title = raw.r ?? UNMEASURED_TITLE;
                }
            },
        });
    });

    return { columns, defaultSortIndex };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function initAllStats(): void {
    const tableEl = document.querySelector<HTMLElement>('.datatable[data-table="mc-all-stats"]');
    if (!tableEl) return; // Only runs on /mc/all-stats

    // Idempotency guard. initAllStats() is called unconditionally from mc.ts's static init() on
    // every /mc/* page; a double-bind would attach two DataTables instances to the same element.
    if (tableEl.dataset.allStatsInited === '1') return;
    tableEl.dataset.allStatsInited = '1';

    // D-03: the column catalog is read from the server, never hardcoded. T-248-08: a malformed or
    // absent data-column-catalog attribute must not crash the rest of the page -- bail out
    // quietly. Deliberately NOT named data-columns -- see the module docblock's collision note.
    let catalog: AllStatsColumn[];
    try {
        const parsed: unknown = JSON.parse(tableEl.dataset.columnCatalog ?? '[]');
        if (!Array.isArray(parsed) || parsed.length === 0) return;
        catalog = parsed as AllStatsColumn[];
    } catch {
        return;
    }

    // Phase 273 D-12/D-13 (Plan 12): extracted to buildAllStatsColumns() above -- see its own
    // docblock for the default-sort flip (D-13's fifth mirrored site) and the per-cell reason
    // widening (D-09/AFK-03).
    const { columns, defaultSortIndex } = buildAllStatsColumns(catalog);

    // D-09/D-10/D-11: bind this page's own new picker id, distinct from every other /mc/*'s own so
    // this page's query can never double-bind a shared module. Read BEFORE the DataTable is
    // constructed below, so currentServers already reflects the deep link by the time the table's
    // first AJAX request fires. Every `server[]` value is read, plus the legacy singular `server`
    // value so an old bookmark still pre-selects (mirroring leaderboard.ts's/feed.ts's/
    // analytics.ts's identical read). CSS.escape guards the attribute selector against each
    // untrusted value (T-249-08-T1); a value with no matching <option> is skipped entirely rather
    // than echoed back -- what the server already did with it.
    const serverSelect = document.getElementById('mc-all-stats-server-select') as HTMLSelectElement | null;
    if (serverSelect) {
        const params = new URLSearchParams(window.location.search);
        const deepLinkServers = [...params.getAll('server[]'), ...params.getAll('server')];
        for (const deepLinkServer of deepLinkServers) {
            const option = serverSelect.querySelector(`option[value="${CSS.escape(deepLinkServer)}"]`);
            if (option) (option as HTMLOptionElement).selected = true;
        }

        currentServers = Array.from(serverSelect.selectedOptions).map((o) => o.value);
    }

    const table = new DataTable('.datatable[data-table="mc-all-stats"]', {
        serverSide: true,
        processing: true,
        pageLength: 25,
        columns,
        order: [[defaultSortIndex, 'desc']],
        ajax: {
            url: '?do=getAllStats',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            data: (data: any) => {
                data.window = currentWindow;
                // D-09/D-12: assign the ARRAY directly -- jQuery's default parameter
                // serialization turns an array-valued key into repeated `server[]` terms, which is
                // exactly the wire format McPresenter::readServerKeys() reads. Never pre-join this
                // into a string and never hand-build a query fragment.
                data.server = currentServers;
                return data;
            },
        },
    });

    // D-09/D-11: picker change listener. Bound exactly once -- this function's own idempotency
    // guard above (tableEl.dataset.allStatsInited) prevents initAllStats() itself from running
    // twice, so this listener cannot double-bind across repeated calls, matching the window-preset
    // block's own single-bind discipline below.
    if (serverSelect) {
        serverSelect.addEventListener('change', () => {
            currentServers = Array.from(serverSelect.selectedOptions).map((o) => o.value);
            syncUrl();
            table.ajax.reload();
        });
    }

    // D-10: the five window preset buttons, scoped to THIS page's own container so this query
    // cannot collide with leaderboard.ts's/analytics.ts's global .mc-range-btn queries.
    const windowsContainer = document.getElementById('mc-all-stats-windows');
    if (windowsContainer) {
        windowsContainer.querySelectorAll<HTMLElement>('.mc-range-btn[data-window]').forEach((btn) => {
            btn.addEventListener('click', () => {
                const key = btn.dataset.window;
                if (!key) return;

                currentWindow = key;

                // Active-class toggle idiom, scoped to this container only (analytics.ts/
                // leaderboard.ts both query .mc-range-btn globally -- this page must not).
                windowsContainer.querySelectorAll('.mc-range-btn').forEach((b) => {
                    b.classList.remove('btn-primary');
                    b.classList.add('btn-outline-secondary');
                });
                btn.classList.remove('btn-outline-secondary');
                btn.classList.add('btn-primary');

                table.ajax.reload();
            });
        });
    }
}

/**
 * D-11: net-new URL persistence -- this page never wrote its server selection back to the address
 * bar before this plan, mirroring leaderboard.ts's/feed.ts's/analytics.ts's identical syncUrl().
 * Deletes BOTH the legacy scalar `server` key and the bracket `server[]` key before appending, so
 * a stale bookmarked scalar can never survive alongside the new list.
 *
 * The selection rides in the URL and NOWHERE else -- neither browser storage API is used anywhere
 * in this module, and nothing is written server-side (T-249-STATE-01). The state-write below is
 * the non-history-appending kind, so a viewer flipping through servers does not have to walk back
 * through every selection to leave the page.
 *
 * Deliberately does NOT touch the window preset -- this plan's scope is the server selection only.
 */
function syncUrl(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete('server');
    url.searchParams.delete('server[]');
    for (const v of currentServers) {
        url.searchParams.append('server[]', v);
    }
    window.history.replaceState(null, '', url.toString());
}
